import jwtDecode from "jwt-decode";
import PlatformService from "@lwolf-common/platform-service/build/PlatformService";
import { getIsTokenExpired, expiredTokenActionByToken, getLwaIdFromTokenInfo } from "../../common/helpers/auth";
import { getKeycloakToken } from "../../service/keycloak";
import { getUserAccounts, getUserAccountsById } from "../../service/account";
import { initialAuthState } from "./store";
import {
    LWA_TOKEN_OBJECT_IDENTIFIER,
    SIGN_UP_MODE,
    getUserTypeByError,
    tokenTypes,
    userTypes
} from "../../constants/auth";
import { msToDays } from "../../common/helpers/utilities";
import {
    urlKeycloakToken,
    urlPlatformConnection,
    urlPlatformLaunch,
} from "../../constants/service";
import {
    navigateToHomeAndReload,
    navigateToPlatformAuth,
    navigateToPlatformUI,
    navigateToPlatformConnection,
    navigateToPlatformHome,
    navigateToValidation,
    navigateToVerificationError,
} from "../../constants/nav";
import {
    PLATFORM_DOMAIN,
    getEndpoint,
    PLATFORM_LOCAL_LAUNCH,
} from "../../providers/endpoint.provider";
import { Routes } from "../../common/routes/routes";
import { sendUserInvitation } from "../../login/components/helpers";
import { useCommonStore, useSavedAgentsStore, useSearchStore } from "../store";
import { ownerTypes, validationMessages as vm } from "../../constants";
import { produce } from 'immer';
import { appIdByAppName, appNames, isAgentMetrics, runningApp } from "../../constants/app";
import { getXamInternalUserPrivileges, getXamUserMls } from "../../service/agent-metrics";
import { getUserProducts } from "../../common/helpers/userPrivileges";
import { setStateData } from "../../common/helpers/state";
import { getProductFlags } from "../../common/helpers/pendo";
import { getIsInternalEmailAddress } from "../../common/helpers/featureToggles";
import { retryEndpoint } from "../../service/misc";
import { topPerformerAgents } from "../../agent-metrics/constants";
import { recordLoginActivity } from '../../service/user-activity';

export const actions = (set, get) => ({
    ...loginActions(set, get),
    incrementAskMeLaterAttempt: () => {
        if (get().isLoadingLinkLwa) return;

        const now = new Date();
        const currentAttempts = get().askMeLaterAttempts;
        set({
            askMeLaterAttempts: currentAttempts + 1,
            lastAskMeLaterAt: now,
            shouldShowLinkLwaModal: false,
        })
    },
    handleShouldShowLinkLwaModal: (hasLwaId) => {
        // This is temporary until we integrate Unified platform with Agent Metrics
        if (isAgentMetrics) return;
        const lastAskedAt = new Date (get().lastAskMeLaterAt);
        const now = new Date();

        const dateDiffInDays = msToDays(now - lastAskedAt);
        const shouldShowByDateDiff = isNaN(dateDiffInDays) || dateDiffInDays > 1;

        if (shouldShowByDateDiff && !hasLwaId) set({shouldShowLinkLwaModal: true});
    },
    sendLwaInvitation: async (user, token) => {
        const userLWAinfo = await sendUserInvitation(user, token);
        return userLWAinfo?.id;
    },
    handleLinkLwa: async (user) => {
        if (get().isLoadingLinkLwa) return;
        try {
            set({ isLoadingLinkLwa: true,  });
            const lwid = await get().sendLwaInvitation(user);

            if(!lwid) {
                get().setInvitationError();
                return;
            };

            set({ isLoadingLinkLwa: false, shouldShowLinkLwaModal: false });

            const redirectUrl = urlPlatformConnection(lwid);
            const authParams = {
                email: user.email,
                redirectUrl,
                mode: SIGN_UP_MODE
            }
            navigateToPlatformAuth(authParams);
        } catch (error) {
            get().setInvitationError();
        }
    },
    setInvitationError: () => {
        set({
            isLoadingLinkLwa: true,
            errorLinkingLwa: vm.unableToLink,
        })
        setTimeout(() => {
            set({
                isLoadingLinkLwa: false,
                shouldShowLinkLwaModal: false,
                errorLinkingLwa: undefined,
            })
        }, 3000);
    },
    authLwaUser: async (token, platformServiceClient) => {
        const tokenInfo = jwtDecode(token);
        const ownerId = getLwaIdFromTokenInfo(tokenInfo).toLowerCase();
        set({
            error: undefined,
            platformServiceClient,
            token,
            tokenInfo: {
                ...tokenInfo,
                [LWA_TOKEN_OBJECT_IDENTIFIER]: {
                    ...tokenInfo[LWA_TOKEN_OBJECT_IDENTIFIER],
                    uid: ownerId
                },
            },
            tokenType: tokenTypes.LWA,
            ownerType: ownerTypes.lwa,
            ownerId
        });
        await recordLoginActivity(token);
    },
    authKeycloakUser: async (state, setState, history, redirectPath) => {
        try {
            useCommonStore.setState({isLoading: true});
            setState(prev => ({
                ...prev,
                isLoading: true,
            }));

            const updateUserType = (userType) => {
                useCommonStore.setState({isLoading: false});
                setState(prev => ({
                    ...prev,
                    userType,
                    isLoading: false
                }));
            };

            const loginUser = async (token, isLegacy) => {
                await recordLoginActivity(token);
                get().setKeycloakToken(token, isLegacy);
                if (isLegacy && redirectPath) {
                    history.replace(redirectPath);
                    window.location.reload();
                    return;
                }
                navigateToHomeAndReload(history);
            };

            const token = await getKeycloakToken(
                urlKeycloakToken,
                state.emailValue,
                encodeURIComponent(state.passwordValue),
            );

            if (token.error) {
                updateUserType(
                    getUserTypeByError[token.error_description]
                        || userTypes.invalid);
                return;
            }

            const accessToken = token.access_token;
            const decodedToken = jwtDecode(accessToken);
            const userAccounts = await getUserAccounts(decodedToken.email, accessToken);

            // This checks when service-gateway is down
            if (userAccounts.error === 503) {
                get().setAuthError(vm.connectionError, history);
                return;
            }

            const isLegacyUser = userAccounts?.error === 404;

            if (isLegacyUser) {
                await loginUser(accessToken, true)
                return;
            }

            const hasLwaId = userAccounts?.user?.lwaId;
            if (hasLwaId) {
                const authParams = {
                    email: state.emailValue,
                    redirectUrl: urlPlatformLaunch
                };
                navigateToPlatformAuth(authParams);
                return;
            };

            //TODO: add account selector logic here, right now select the first option
            const selectedAccount = userAccounts.accounts[0];
            const isInactiveXbmUser = !selectedAccount.accountActive || !selectedAccount.userActive;

            if (isInactiveXbmUser) return updateUserType(userTypes.inactiveXbm);

            useCommonStore.setState({isLoading: false});
            setState(prev => ({
                ...prev,
                userType: undefined,
                isLoading: false,
            }));

            loginUser(accessToken);
        } catch (error) {
            console.log({error});
            useCommonStore.setState({isLoading: false});
            setState(prev => ({
                ...prev,
                isLoading: false,
            }));
            set({
                authError: vm.unknownError,
                token: undefined,
                tokenInfo: undefined,
                tokenType: undefined,
                platformServiceClient: undefined,
            });
        }
    },
    getXamInternalUserPrivileges: async ({ history }) => {
        const userPrivileges = await retryEndpoint({ fetchFunction: getXamInternalUserPrivileges});
        const officeInfoPayload = userPrivileges.flatMap(({ mlsId, officeId }) =>
            officeId.map(id => ({ mlsId, officeId: id }))
        );
        const mlsIds = userPrivileges.map(({ mlsId }) => mlsId);
        const activeOffices = await useCommonStore.getState().getOfficeInfo(officeInfoPayload);
        if (!mlsIds?.length || mlsIds?.length === 0 || !activeOffices?.length || activeOffices?.length === 0) {
            navigateToVerificationError(history);
            return { isValidUser: false };
        }
        const agentInfo = useCommonStore.getState().agentInfo;
        if (!agentInfo) {
            const defaultAgentProductionAgent = [{
                agentId: topPerformerAgents[0].agentId,
                mlsId: topPerformerAgents[0].mlsId
            }];
            await useCommonStore.getState().getAgentInfo(defaultAgentProductionAgent);
        }
        return { isValidUser: true, mlsIds, activeOffices };
    },
    getXamExternalUserPrivileges: async ({ history, platformToken, activeClientId, }) => {
        let activeAgents, activeOffices, mlsIds, userMls;
        userMls = await (platformToken && activeClientId
            ? getXamUserMls(platformToken, activeClientId)
            : getXamUserMls()
        );
        if (userMls?.error || !userMls) {
            navigateToPlatformHome();
            return { isValidUser: false };
        }
        if (userMls?.list.length === 0) {
            navigateToValidation(history);
            return { isValidUser: true };
        }
        const mlsAgents = userMls.list.map(({ agentId, mlsId }) => ({
            agentId,
            mlsId,
        }));
        const agentInfo = await useCommonStore.getState().getAgentInfo(mlsAgents);
        activeAgents = agentInfo.length > 0 && agentInfo.filter(agent => agent.isAgentActive);
        if (!activeAgents?.length || activeAgents?.length === 0) {
            navigateToVerificationError(history);
            return { isValidUser: false };
        }
        activeOffices = activeAgents.map(agent => agent.office);
        mlsIds = activeAgents.map(agentOffice => agentOffice.mlsAgentId.mlsId);
        return { isValidUser: true, mlsIds, activeOffices };
    },
    getXamUserPrivileges: async ({isInternalUser, history, activeClientId, platformToken}) => {
        return isInternalUser
            ? await get().getXamInternalUserPrivileges({ history })
            : await get().getXamExternalUserPrivileges({
                history,
                activeClientId,
                platformToken
            });
    },
    verifyAgentMetricsUser: async ({
        platformToken,
        activeClientId,
        history,
        isInternalUser,
    }) => {
        if (!activeClientId) return { isValidUser: false };
        get().setActiveClientId(activeClientId);
        set({ token: platformToken });

        try {
            const { isValidUser } = await get().getXamUserPrivileges({
                isInternalUser,
                history,
                activeClientId,
                platformToken
            });
            return { isValidUser };
        } catch (error) {
            console.error(error);
            navigateToPlatformHome();
            return { isValidUser: false };
        }
    },
    verifyBrokerMetricsUser: async ({
        platformToken,
        platformTokenInfo,
        incomingLwid,
        history,
    }) => {
        const xbmUserIdFromToken = platformTokenInfo[LWA_TOKEN_OBJECT_IDENTIFIER]?.apps?.xbm?.uid;

        if (!xbmUserIdFromToken) {
            navigateToPlatformUI();
            return false;
        }

        const userAccounts = await getUserAccountsById(xbmUserIdFromToken, incomingLwid, platformToken);
        const isLwaUser = userAccounts?.user?.lwaId;

        if( useCommonStore.getState()?.userInfo?.id === xbmUserIdFromToken) {
            navigateToHomeAndReload(history);
            return false;
        }

        if (!isLwaUser) {
            get().setAuthErrorByRoute(vm.connectionMismatch(platformTokenInfo.email), Routes.AUTH.LOG_IN, history);
            return false;
        }

        return true;
    },
    setKeycloakToken: (token, isLegacyUser = false) => {
        const tokenInfo = jwtDecode(token);
        const ownerId = tokenInfo?.sub;
        set({
            token,
            isLegacyUser,
            tokenInfo: jwtDecode(token),
            tokenType: tokenTypes.KEYCLOAK,
            ownerType: ownerTypes.user,
            ownerId,
        })
    },
    clearToken: () => set({
        token: undefined,
        tokenInfo: undefined,
        tokenTypes: undefined
    }),
    initPlatform: async ({history, activeClientId, redirectPath}) => {
        try {
            const previousOwnerId = get().ownerId;
            set({ authError: undefined });
            get().resetStore();
            useCommonStore.getState().resetStore();

            const platformService = await PlatformService.initialize({
                lwDomain: getEndpoint(PLATFORM_DOMAIN),
                appId: appIdByAppName[runningApp],
                lwaOnly: true,
                luParamSupplier: () => getEndpoint(PLATFORM_LOCAL_LAUNCH)
            });

            const platformToken = platformService.validateAndGetToken();
            const platformTokenInfo = jwtDecode(platformToken);
            const incomingLwid = platformTokenInfo?.[LWA_TOKEN_OBJECT_IDENTIFIER]?.uid.toLowerCase();
            const isInternalUser = getIsInternalEmailAddress(platformTokenInfo?.email);
            const isSameUser = !!previousOwnerId && previousOwnerId === incomingLwid;

            useCommonStore.getState().resetStore();
            if (!isSameUser) {
                useSearchStore.getState().resetStore();
            }

             let isValidUser, shouldGoToValidation;
            if (runningApp === appNames.XBM) {
                isValidUser = await get().verifyBrokerMetricsUser({
                    platformToken,
                    platformTokenInfo,
                    incomingLwid,
                    history,
                });
            }
            if (runningApp === appNames.XAM) {
                const agentMetricsVerification = await get().verifyAgentMetricsUser({
                    platformToken,
                    activeClientId,
                    history,
                    isInternalUser,
                });
                isValidUser = agentMetricsVerification.isValidUser;
            }
            if (!isValidUser) return;

            get().authLwaUser(platformToken, platformService);

            if (shouldGoToValidation) {
                navigateToValidation(history);
            }

            if (redirectPath) {
                history.replace(redirectPath);
                return;
            }

            navigateToHomeAndReload(history);
        } catch (error) {
            console.log({error});
            navigateToPlatformUI();
        }
    },
    initAgentMetricsApp: async ({
        setState,
        setPendoDataLayers,
        tokenInfo,
        getUserLists,
        history,
        isLegacyUser,
        runningApp,
        ownerId,
        isAgentProductionEnabled,
    }) => {
        const userProducts = getUserProducts({isAgentMetricsUser: true, isAgentProductionEnabled });
        setStateData('allowedModules', userProducts, setState);
        const productFlags = getProductFlags(userProducts);
        setPendoDataLayers({ tokenInfo, userProducts, productFlags, isLegacyUser, runningApp, ownerId });

        const commonStore = useCommonStore.getState();
        const isInternalUser = getIsInternalEmailAddress(tokenInfo.email);
        const { isValidUser, mlsIds, activeOffices } = await get().getXamUserPrivileges({isInternalUser, history});
        if (!isValidUser) return;
        useCommonStore.setState({ officeInfo: activeOffices });
        await commonStore.getMembership(mlsIds);
        await commonStore.getTimeIntervals();
        await getUserLists(ownerId);
    },
    setAuthErrorByRoute: (errorMessage, route, history) => {
        set(produce(state => {
            state.authErrorByRoute[route] = errorMessage;
            state.token = undefined;
            state.tokenInfo = undefined;
            state.platformServiceClient = undefined;
        }));
        history && history.replace(Routes.AUTH.LOG_IN);
    },
    logout: (history) => {
        useCommonStore.getState().resetStore();
        useSearchStore.getState().resetStore();
        useSavedAgentsStore.getState().resetStore();
        get().resetStore();
        history.replace(Routes.AUTH.LOG_IN);
        window.location.reload();
    },
    handleExpiredToken: () => {
        const tokenInfo = get().tokenInfo;
        const tokenType = get().tokenType;

        const isTokenExpired = getIsTokenExpired(tokenInfo);

        if(!isTokenExpired) return;

        expiredTokenActionByToken[tokenType]();
    },
    setActiveClientId: activeClientId => set({activeClientId}),
    resetStore: () => {
        const askMeLaterAttempts = get().askMeLaterAttempts;
        const lastAskMeLaterAt = get().lastAskMeLaterAt;
        set({...initialAuthState, askMeLaterAttempts, lastAskMeLaterAt});
    }
});

const loginActions = (set, get) => ({
    setIsLoadingByRoute: (route, isLoading) => {
        set(produce(state => {
            state.isLoadingByRoute[route] = isLoading;
        }));
    },
    getUserByEmail: async (state, setState, currentRoute) => {
        try {
            const token = await getKeycloakToken(
                urlKeycloakToken,
                state.emailValue,
                encodeURIComponent(state.passwordValue),
            );

            if (token.error) {
                get().updateUserType(
                    getUserTypeByError[token.error_description]
                        || userTypes.invalidLogin, setState);
                return;
            }

            const accessToken = token.access_token;
            const decodedToken = jwtDecode(accessToken);
            const userAccounts = await getUserAccounts(decodedToken.email, accessToken);

            // This checks when service-gateway is down
            if (userAccounts.error === 503) {
                get().authErrorByRoute(vm.connectionError, currentRoute, history);
                return;
            }

            return { accessToken, userAccounts };
        } catch (error) {
            console.error(error);
        } finally {
            get().setIsLoadingByRoute(currentRoute, false);
        }
    },
    updateUserType: (userType, setState) => {
        setState(prev => ({
            ...prev,
            userType,
        }));
    },
    loginUser: async (token, history) => {
        get().setKeycloakToken(token);
        await recordLoginActivity(token);
        navigateToHomeAndReload(history);
    },
    setKeycloakToken: (token) => set({
        token,
        tokenInfo: jwtDecode(token),
        tokenType: tokenTypes.KEYCLOAK,
    }),
    authKeycloakUser: async (state, setState, history) => {
        const currentRoute = Routes.AUTH.LOG_IN;
        const setIsLoading = (isLoading) => {
            get().setIsLoadingByRoute(currentRoute, isLoading);
        }
        try {
            setIsLoading(true);
            const { accessToken, userAccounts } = await get().getUserByEmail(state, setState, currentRoute);

            const isLegacyUser = userAccounts?.error === 404;

            if (isLegacyUser) {
                get().loginUser(accessToken, history);
                return;
            }

            const hasLwaId = userAccounts?.user?.lwaId;
            if (hasLwaId) {
                const authParams = {
                    email: state.emailValue,
                    redirectUrl: urlPlatformLaunch
                };
                navigateToPlatformAuth(authParams);
                return;
            };

            //TODO: add account selector logic here, right now select the first option
            const selectedAccount = userAccounts.accounts[0];
            const isInactiveXbmUser = !selectedAccount.accountActive || !selectedAccount.userActive;

            if (isInactiveXbmUser) {
                get().updateUserType(userTypes.inactiveXbm, setState)
                setIsLoading(false);
                return;
            };

            setState(prev => ({
                ...prev,
                userType: undefined,
            }));

            get().loginUser(accessToken, history);
        } catch (error) {
            console.log({error});
            get().setAuthErrorByRoute(vm.unknownError, currentRoute);
        } finally {
            setIsLoading(false);
        }
    },
    connectKeycloakUser: async (state, setState) => {
        const currentRoute = Routes.AUTH.CONNECT;
        if (get().isLoadingByRoute[currentRoute]) return;

        const setIsLoading = (isLoading) => {
            get().setIsLoadingByRoute(currentRoute, isLoading);
        };

        const setAuthError = (errorMessage) => {
            get().setAuthErrorByRoute(errorMessage, currentRoute);
        };

        try {
            setIsLoading(true);
            const { accessToken, userAccounts } = await get().getUserByEmail(state, setState, currentRoute);

            if(!userAccounts) return;

            const isLegacyUser = userAccounts?.error === 404;

            if (isLegacyUser) {
                setAuthError(vm.unableToConnect);
                setIsLoading(false);
                return;
            }

             //TODO: add account selector logic here, right now select the first option
             const selectedAccount = userAccounts.accounts[0];
             const isInactiveXbmUser = !selectedAccount.accountActive || !selectedAccount.userActive;

             if (isInactiveXbmUser) {
                get().updateUserType(userTypes.inactiveXbm, setState);
                setIsLoading(false);
                return;
            }

            const hasLwaId = userAccounts?.user?.lwaId;

            if (hasLwaId) {
                setAuthError(vm.accountAlreadyConnected);
                setIsLoading(false);
                return;
            }

            const userInfo = userAccounts.user;
            const lwaId = await get().sendLwaInvitation(userInfo, accessToken);

            if(!lwaId) {
                setAuthError(vm.unableToLink);
                setIsLoading(false);
                return;
            }

            navigateToPlatformConnection(lwaId);

            get().updateUserType(undefined, setState);
        } catch (error) {
            console.error(error);
            setAuthError(vm.unknownError);
            setIsLoading(false);
        }
    },
    setIsXamUserValidated: (isValidated) => set({ isXamUserValidated: isValidated }),
});
